import { defineDirective } from '~/scripts/utils/alpine'

const ACTION = 'form_submit'
const TIMEOUT = 10_000

const withTimeout = async <T>(promise: Promise<T>) => {
  let timeoutId: number | undefined

  try {
    return await Promise.race<T>([
      promise,
      new Promise<T>((_resolve, reject) => {
        timeoutId = globalThis.setTimeout(
          () => reject(new Error('reCAPTCHA timed out')),
          TIMEOUT,
        )
      }),
    ])
  } finally {
    globalThis.clearTimeout(timeoutId)
  }
}

export default defineDirective((input, { expression: key }, { cleanup }) => {
  if (!key || typeof key !== 'string') return
  if (!(input instanceof HTMLInputElement)) return
  const form = input.closest('form')
  if (!form) return

  const errorMessage = input.nextElementSibling
  let isSubmitting = false
  let hasToken = false

  const hideError = () => {
    if (!(errorMessage instanceof HTMLElement)) return

    errorMessage.hidden = true
  }

  const showError = () => {
    if (!(errorMessage instanceof HTMLElement)) return

    errorMessage.hidden = false
  }

  const onSubmit = async (event: SubmitEvent) => {
    if (hasToken) {
      hasToken = false
      return
    }

    event.preventDefault()

    if (isSubmitting) return

    isSubmitting = true
    input.value = ''
    hideError()
    form.setAttribute('aria-busy', 'true')

    try {
      const { load } = await import('recaptcha-v3')
      const recaptcha = await withTimeout(load(key))
      const token = await withTimeout(recaptcha.execute(ACTION))

      if (!token) throw new Error('reCAPTCHA returned an empty token')

      input.value = token
      hasToken = true

      const submitter = event.submitter

      if (
        submitter instanceof HTMLButtonElement ||
        submitter instanceof HTMLInputElement
      ) {
        form.requestSubmit(submitter)
      } else {
        form.requestSubmit()
      }
    } catch {
      input.value = ''
      showError()
    } finally {
      isSubmitting = false
      form.removeAttribute('aria-busy')
    }
  }

  form.addEventListener('submit', onSubmit)

  cleanup(() => {
    form.removeEventListener('submit', onSubmit)
  })
})
